import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import * as _ from 'lodash';

import { isNotEmpty, isNotNull, isUUID, ValidateParameters } from '../../parameter-validators';
import { Device, DeviceAddress, DeviceLocation } from './models/device.interface';
import { HeaderForAuthentication } from '../auth/model/header-for-authentication.model';
import { Site } from './models/site.interface';
import { DeviceLicenses } from './models/device-licenses.interface';
import { FileConverter } from '../../file-converter.service';
import { AccountRights } from '../auth/model/account-rights.interface';

export class DeviceApiBaseService {

  constructor(protected http: HttpClient) {
  }

  protected attachExtendedMetaData(device: Device, accountId: string): Device {
    device.accountId = accountId;

    if (device.status.heartbeatState === 'ACTIVE') {
      device.status.lastHeartbeatDate = new Date();
    }

    const switchMask = 0x00020000;
    const lcosLxSdk4SwitchMask = 0x02000000;
    const accessPointMask = 0x08000000;
    const lcosLxAccessPointMask = 0x00040000;
    const unifiedFirewallMask = 0x0080000;

    /* tslint:disable:no-bitwise */
    if (device.status.hwMask & switchMask || device.status.hwMask & lcosLxSdk4SwitchMask) {
      device.deviceType = 'SWITCH';
    } else if (device.status.hwMask & lcosLxAccessPointMask) {
      device.deviceType = 'ACCESSPOINT_LX';
    } else if (device.status.hwMask & accessPointMask) {
      device.deviceType = 'ACCESSPOINT';
    } else if (device.status.hwMask & unifiedFirewallMask) {
      device.deviceType = 'UNIFIED_FIREWALL';
    } else {
      device.deviceType = 'VPN';
    }

    if (device.status.model === 'LANCOM vRouter') {
      device.deviceType = 'VPN';
    }
    /* tslint:disable-next-line:no-bitwise */

    if (device.status) {
      const minorLength = 2;
      const buildLength = 4;

      const major = _.get(device, 'status.fwMajor', 0);
      const minorRaw = _.get(device, 'status.fwMinor', 0);
      const buildRaw = _.get(device, 'status.fwBuild', 0);

      const minor = _.padStart(minorRaw, minorLength, '0');
      const build = _.padStart(buildRaw, buildLength, '0');

      device.status.fwVersion = major + '.' + minor + '.' + build;
    }

    return device;
  }
}

@Injectable({ providedIn: 'root' })
export class DeviceApiService extends DeviceApiBaseService {
  constructor(http: HttpClient, private fileConverter: FileConverter) {
    super(http);
  }

  @ValidateParameters
  readDevice$(@isUUID accountId: string, @isUUID deviceId: string): Observable<Device> {
    const url = `/cloud-service-devices/accounts/${accountId}/devices/${deviceId}`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.get<Device>(url, options).pipe(
      map(device => {
        this.attachExtendedMetaData(device, accountId);
        // this.attachImageMetaData(device); // needed when we want to get the image to a device but right now we only want to set it
        return device;
      }),
    );
  }

  @ValidateParameters
  readDeviceBySerial$(@isUUID accountId: string, @isNotEmpty serial: string): Observable<Device> {
    const url = `/cloud-service-devices/accounts/${accountId}/devices`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
      params: {
        'status.serial': serial,
      },
    };

    return this.http.get<Device[]>(url, options).pipe(
      switchMap(devices => devices.length === 1 ? of(devices[0]) : throwError('Unexpected result size')),
    );
  }

  @ValidateParameters
  updateDevice$(
    @isUUID accountId: string,
    @isUUID deviceId: string,
    @isNotEmpty deviceData: Device,
  ): Observable<Device> {
    const url = `/cloud-service-devices/accounts/${accountId}/devices/${deviceId}`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };

    return this.http.post<Device>(url, deviceData, options);
  }

  @ValidateParameters
  claimDevice$(
    @isUUID accountId: string,
    @isNotEmpty serial: string,
    @isNotNull pin: string,
    name: string,
    siteId: string,
    location: DeviceLocation,
    address: DeviceAddress,
  ): Observable<Device> {
    const claimingData = {
      serial,
      pin,
      siteId,
      name,
      location,
      address,
    };
    const url = `/cloud-service-devices/accounts/${accountId}/devices`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };

    return this.http.post<Device>(url, claimingData, options);
  }

  readAccountsRights$(accountIds: string[]): Observable<AccountRights[]> {
    const url = '/cloud-service-devices/rights/accounts';
    const maxAccountAmountPerCall = 50; // 75 is the limit of the backend. 76 >= results in a 400 error because the auth header is to long
    const accountRightCallLists = _.chunk(accountIds, maxAccountAmountPerCall);

    const requests = accountRightCallLists.map((currentAccountIds: string[]) => {
      const options = {
        headers: new HeaderForAuthentication(currentAccountIds),
      };

      return this.http.get<AccountRights[]>(url, options);
    });

    return forkJoin(requests).pipe(
      map((rights: AccountRights[][]) => {
        return _.flatten(rights);
      }));
  }

  readAccountRights$(accountId: string): Observable<String[]> {
    const url = `/cloud-service-devices/rights/accounts/${accountId}`;

    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };

    return this.http.get<String[]>(url, options);
  }

  // SITE API calls - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  @ValidateParameters
  readSiteIds$(@isUUID accountId: string): Observable<string[]> {
    const url = '/cloud-service-devices/accounts/' + accountId + '/sites/ids?select=id&sort=name,asc';
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.get<string[]>(url, options);
  }

  @ValidateParameters
  readSiteIdsFiltered$(@isUUID accountId: string, @isNotEmpty nameLike: string): Observable<string[]> {
    const url = '/cloud-service-devices/accounts/' + accountId + '/sites/ids?select=id&sort=name,asc&nameILike=' + nameLike;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.get<string[]>(url, options);
  }

  @ValidateParameters
  readSites$(@isUUID accountId: string, ids: string[]): Observable<Site[]> {
    let idString = '';
    ids.forEach(id => {
      idString += '&id=' + id;
    });
    const url = '/cloud-service-devices/accounts/' + accountId + '/sites?property=name,id&sort=name,asc' + idString;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.get<Site[]>(url, options);
  }

  @ValidateParameters
  readSite$(@isUUID accountId: string, @isUUID siteId: string): Observable<Site> {
    const url = `/cloud-service-devices/accounts/${accountId}/sites/${siteId}`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.get<Site>(url, options);
  }

  @ValidateParameters
  updateDeviceImage$(@isUUID accountId: string, @isUUID deviceId: string, imageFile: File | Blob): Observable<void> {
    const url = `/cloud-service-devices/accounts/${accountId}/devices/${deviceId}/image`;
    const data = this.fileConverter.fileToFormData(imageFile);
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.post<void>(url, data, options);
  }

  @ValidateParameters
  readLicenseCategory$(@isUUID accountId: string, @isUUID deviceId: string): Observable<DeviceLicenses> {
    const url = `/cloud-service-devices/accounts/${accountId}/license-options`;
    const data = {deviceIds: [deviceId]}
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.post<DeviceLicenses>(url, data, options);
  }

  @ValidateParameters
  allocateLicense$(@isUUID accountId: string, @isUUID deviceId: string, @isUUID poolId: string): Observable<void> {
    const deviceLicenseDateCreate = {
      "devices": [
        {
          "deviceId": deviceId,
          "poolId": poolId
        }
      ],
      "strategy": "SHORTEST_FIRST"
    }
    const url = `/cloud-service-devices/accounts/${accountId}/devices/licenses`;
    const options = {
      headers: new HeaderForAuthentication([accountId]),
    };
    return this.http.post<void>(url, deviceLicenseDateCreate, options);
  }

}
